import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiClient } from '../services/apiClient';

export enum UserRole {
  GUEST = 'guest',
  MEMBER = 'member',
  TOOL_USER = 'tool_user',
  PREMIUM = 'premium',
  MODERATOR = 'moderator',
  ADMIN = 'admin'
}

export interface User {
  id: string;
  email: string;
  fullName: string;
  isActive: boolean;
  role: UserRole;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  isLoading: boolean;
  login: (userData: User) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  user: null,
  isLoading: true,
  login: () => {},
  logout: () => {},
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Thiết lập callback xử lý token hết hạn
  useEffect(() => {
    apiClient.setTokenExpiredCallback(() => {
      setUser(null);
      setIsAuthenticated(false);
      setIsLoading(false);
    });
  }, []);

  // Tải trạng thái xác thực từ localStorage khi gắn kết
  useEffect(() => {
    const loadAuthState = () => {
      const savedAuth = localStorage.getItem('auth');
      if (savedAuth) {
        try {
          const { user: savedUser } = JSON.parse(savedAuth);
          setUser(savedUser);
          setIsAuthenticated(true);
        } catch (error) {
          localStorage.removeItem('auth');
        }
      }
    };

    // Tải trạng thái ban đầu
    loadAuthState();
    
    // Thêm một delay nhỏ để đảm bảo tab mới có thời gian load
    const timeoutId = setTimeout(() => {
      loadAuthState();
      setIsLoading(false);
    }, 100);
    
    // Đặt tải thành false sau thời gian chờ hợp lý
    const loadingTimeout = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    // Lắng nghe sự thay đổi trong storage (khi trạng thái auth thay đổi trong các tab khác)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'auth') {
        if (e.newValue) {
          try {
            const { user: savedUser } = JSON.parse(e.newValue);
            setUser(savedUser);
            setIsAuthenticated(true);
          } catch (error) {
          }
        } else {
          setUser(null);
          setIsAuthenticated(false);
        }
      }
    };

    // Lắng nghe postMessage từ các tab khác
    const handleMessage = (e: MessageEvent) => {
      if (e.data && e.data.type === 'AUTH_SYNC') {
        if (e.data.authData) {
          try {
            const { user: savedUser } = JSON.parse(e.data.authData);
            setUser(savedUser);
            setIsAuthenticated(true);
          } catch (error) {
          }
        } else {
          setUser(null);
          setIsAuthenticated(false);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('message', handleMessage);

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(loadingTimeout);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  const login = (userData: User) => {
    setUser(userData);
    setIsAuthenticated(true);
    setIsLoading(false);
    localStorage.setItem('auth', JSON.stringify({ user: userData }));
    
    // Trigger storage event để các tab khác cập nhật
    window.dispatchEvent(new StorageEvent('storage', {
      key: 'auth',
      newValue: JSON.stringify({ user: userData }),
      oldValue: null
    }));
    
    // Gửi postMessage đến tất cả tab khác
    window.postMessage({
      type: 'AUTH_SYNC',
      authData: JSON.stringify({ user: userData })
    }, window.location.origin);
  };

  const logout = () => {
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('auth');
    
    // Trigger storage event để các tab khác cập nhật
    window.dispatchEvent(new StorageEvent('storage', {
      key: 'auth',
      newValue: null,
      oldValue: localStorage.getItem('auth')
    }));
    
    // Gửi postMessage đến tất cả tab khác
    window.postMessage({
      type: 'AUTH_SYNC',
      authData: null
    }, window.location.origin);
  };

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      user, 
      isLoading,
      login, 
      logout
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};